/* INCÓGNITA — background video autoplay helper
 *
 * Mobile browsers refuse or silently drop autoplay in a bunch of situations:
 * iOS Low Power Mode, Android Data Saver, the tab starting in the background,
 * the video starting outside the viewport, HEVC being "supported" by
 * canPlayType on Android but failing to decode, and Dawn's pauseAllMedia()
 * pausing every <video> whenever a modal opens.
 *
 * This element wraps a muted looping <video> and keeps nudging it to play.
 */
(function () {
  'use strict';

  if (customElements.get('inc-bg-video')) return;

  class IncBgVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;

      const v = this.video;
      // Set as properties too: some browsers ignore the HTML attributes when
      // the markup is injected late (theme editor, section rendering API).
      v.muted = true;
      v.defaultMuted = true;
      v.loop = true;
      v.playsInline = true;
      v.setAttribute('playsinline', '');
      v.setAttribute('data-inc-bg-video', '');

      this.inView = true;
      this.tryPlay = this.tryPlay.bind(this);
      this.onGesture = this.onGesture.bind(this);
      this.onVisibility = this.onVisibility.bind(this);
      this.onError = this.onError.bind(this);

      // Fall back to the next <source> when the chosen one fails to decode
      // (Android devices that advertise HEVC and then choke on it).
      v.addEventListener('error', this.onError);
      v.querySelectorAll('source').forEach((s) => s.addEventListener('error', this.onError));

      // Play only while visible; saves battery and avoids "started offscreen
      // and never resumed" on Safari.
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.inView = entry.isIntersecting;
              if (entry.isIntersecting) this.tryPlay();
              else if (!v.paused) v.pause();
            });
          },
          { threshold: 0.1 }
        );
        this.io.observe(v);
      }

      // Autoplay blocked (Low Power Mode etc.): the first gesture unlocks it.
      ['touchstart', 'touchend', 'click', 'scroll', 'keydown'].forEach((evt) =>
        window.addEventListener(evt, this.onGesture, { passive: true })
      );
      document.addEventListener('visibilitychange', this.onVisibility);
      // Dawn's modal-dialog pauses every video on open and close.
      document.body.addEventListener('modalClosed', this.tryPlay);

      v.addEventListener('loadedmetadata', this.tryPlay, { once: true });
      v.addEventListener('canplay', this.tryPlay);
      v.addEventListener('pause', () => {
        // Something else paused us while we are on screen: come back.
        if (this.inView && !document.hidden && !v.ended) {
          clearTimeout(this.resumeTimer);
          this.resumeTimer = setTimeout(this.tryPlay, 400);
        }
      });

      this.tryPlay();
    }

    disconnectedCallback() {
      if (this.io) this.io.disconnect();
      ['touchstart', 'touchend', 'click', 'scroll', 'keydown'].forEach((evt) =>
        window.removeEventListener(evt, this.onGesture)
      );
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.body.removeEventListener('modalClosed', this.tryPlay);
      clearTimeout(this.resumeTimer);
    }

    tryPlay() {
      const v = this.video;
      if (!v || !this.inView || document.hidden) return;
      if (!v.paused && !v.ended) return;
      v.muted = true;
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    }

    onGesture() {
      if (this.video && this.video.paused) this.tryPlay();
    }

    onVisibility() {
      if (!document.hidden) this.tryPlay();
    }

    onError(e) {
      const v = this.video;
      if (!v) return;
      const sources = Array.from(v.querySelectorAll('source'));
      // <source> errors are handled natively by moving on to the next source;
      // we only need to act when the <video> itself errors out after picking one.
      if (e.target !== v || !v.error) return;
      if (this.fellBack) return;
      const current = sources.find((s) => v.currentSrc && v.currentSrc.indexOf(s.getAttribute('src')) !== -1);
      const idx = current ? sources.indexOf(current) : -1;
      const next = sources[idx + 1];
      if (!next) return;
      this.fellBack = true;
      v.removeAttribute('src');
      sources.slice(0, idx + 1).forEach((s) => s.remove());
      v.load();
      this.tryPlay();
    }
  }

  customElements.define('inc-bg-video', IncBgVideo);
})();
